<template>
  <div class="project-detail-container">
    <v-row class="project-detail-panel" dense>
      <!-- Project Profile  -->
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="flex-column align-center pt-6">
            <v-avatar :color="projectDataLocal.color || 'primary'" size="120" rounded elevation="3" class="mb-4">
              <span class="white--text text-h4 font-weight-bold">{{ projectDataLocal.job_number }}</span>
            </v-avatar>

            <h3 class="text-h6 mb-2">{{ projectDataLocal.job_name }}</h3>

            <v-chip
              small label
              :color="statusColor"
              :class="`v-chip-light-bg ${statusColor}--text font-weight-bold text-capitalize`"
            >
              {{ projectDataLocal.job_status.name }}
            </v-chip>
          </v-card-title>

          <v-card-text class="d-flex justify-center flex-wrap py-4">
            <v-tooltip top v-for="(action, index) in headerActions" :key="index">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon class="mx-2"
                  v-bind="attrs" v-on="on"
                  :loading="action.loading"
                  @click="action.handler"
                >
                  <v-avatar size="42" rounded color="primary" class="v-avatar-light-bg primary--text">
                    <v-icon size="22">{{ action.icon }}</v-icon>
                  </v-avatar>
                </v-btn>
              </template>
              <span>{{ action.label }}</span>
            </v-tooltip>
          </v-card-text>

          <v-divider class="mx-4"></v-divider>

          <!-- Project Details List -->
          <v-card-text>
            <div class="d-flex align-center mb-3">
              <span class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Details</span>
              <v-btn icon small @click="toggleFavorite" class="ml-2">
                <v-icon :color="projectDataLocal.is_favorite ? 'error' : ''" small>
                  {{ projectDataLocal.is_favorite ? icons.mdiHeart : icons.mdiHeartOutline }}
                </v-icon>
              </v-btn>
            </div>
            
            <v-list dense class="pa-0">
              <v-list-item v-for="(item, i) in detailFields" :key="i" class="px-0 min-height-32">
                <span class="font-weight-semibold me-2 text-body-2">{{ item.label }}:</span>
                <span class="text--secondary text-body-2">{{ item.value }}</span>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Project Budget -->
      <v-col cols="12">
        <v-card :style="budgetBorderStyle" flat outlined>
          <v-card-title class="d-flex justify-space-between align-end">
            <span class="text-subtitle-1 grey--text">Budget</span>
            <div class="project-pricing">
              <span :class="`text-h4 font-weight-bold ${statusColor}--text` ">
                {{ formatNumber(projectDataLocal.budget) }}
              </span>
              <sup :class="`ml-1 ${statusColor}--text font-weight-bold` ">{{ projectDataLocal.currency.name }}</sup>
            </div>
          </v-card-title>

          <v-card-text>
            <v-list dense class="pa-0 mb-4">
              <v-list-item v-for="(item, i) in budgetFields" :key="i" class="px-0 min-height-32" v-show="item.show !== false">
                <v-icon size="10" class="me-2" :color="statusColor">{{ icons.mdiCheckboxBlankCircle }}</v-icon>
                <span class="font-weight-medium me-2 text-body-2">{{ item.label }}:</span>
                <span class="text--secondary text-body-2">{{ item.value }}</span>
              </v-list-item>
            </v-list>

            <v-btn
              :color="statusColor"
              block depressed
              :disabled="!projectDataLocal.editable"
              @click="isFinancialDialogOpen = true"
              v-if="$can('add', 'project')"
            >
              Update Financials
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <project-financial-edit
      :projectData="projectData"
      :is-financial-dialog-open.sync="isFinancialDialogOpen"
      @update-project="updateProject"
    />
  </div>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api'
import { 
  mdiCheck, mdiMicrosoftSharepoint, mdiCheckboxBlankCircle, 
  mdiChartTimeline, mdiFileExcelOutline, mdiHeartOutline, 
  mdiHeart, mdiImageText 
} from '@mdi/js'
import { goToLink } from '@core/utils'
import useProjectsList from '../../project-list/useProjectsList'
import useProject from '../../useProject'
import ProjectFinancialEdit from './ProjectFinancialEdit.vue'
import router from '@/router'

export default {
  components: { ProjectFinancialEdit },
  props: {
    projectData: { type: Object, required: true },
  },
  setup(props, { emit }) {
    const isFinancialDialogOpen = ref(false)
    const projectDataLocal = ref(JSON.parse(JSON.stringify(props.projectData)))

    const { resolveProjectStatusVariant } = useProjectsList()
    const { exportLoading, downloadTimeline, addFavoriteProject, deleteFavoriteProject } = useProject()

    const statusColor = computed(() => resolveProjectStatusVariant(projectDataLocal.value.job_status.name))
    
    const budgetBorderStyle = computed(() => ({
      border: `2px solid var(--v-${statusColor.value}-base) !important`
    }))

    const formatNumber = (val) => (val ? val.toLocaleString('fr-FR') : '0')

    const detailFields = computed(() => [
      { label: 'Client', value: projectDataLocal.value.client?.name },
      { label: 'Brand', value: projectDataLocal.value.brand },
      { label: 'Category', value: projectDataLocal.value.category?.name },
      { label: 'Office', value: projectDataLocal.value.office?.name },
      { label: 'Contact', value: projectDataLocal.value.contact?.name },
    ])

    const budgetFields = computed(() => [
      { label: 'Billing Rule', value: projectDataLocal.value.billing_rule?.name },
      { 
        label: 'Gross Margin', 
        value: formatNumber(projectDataLocal.value.gross_margin || projectDataLocal.value.estimated_gross_margin) 
      },
      { label: 'OOP', value: formatNumber(projectDataLocal.value.oop) },
      { label: 'PO', value: projectDataLocal.value.po },
      { label: 'Job Book', value: projectDataLocal.value.ges_bva, show: !!projectDataLocal.value.ges_bva },
      { label: 'Billed By', value: projectDataLocal.value.billed_by?.name, show: !!projectDataLocal.value.billed_by },
    ])

    const headerActions = computed(() => [
      { 
        label: 'Timeline Page', 
        icon: mdiChartTimeline, 
        handler: () => {
          const route = router.resolve({ path: `/timeline/project/${projectDataLocal.value.job_number}` })
          window.open(route.href, '_blank')
        } 
      },
      { 
        label: 'Sharepoint', 
        icon: mdiMicrosoftSharepoint, 
        handler: () => goToLink(projectDataLocal.value.folder_web_url) 
      },
      { 
        label: 'Export Timeline', 
        icon: mdiFileExcelOutline, 
        loading: exportLoading.value,
        handler: () => downloadTimeline(projectDataLocal.value) 
      },
    ])

    const updateProject = (data) => emit('update-project', data)

    const toggleFavorite = async () => {
      const payload = { project: projectDataLocal.value.job_number }
      if (projectDataLocal.value.is_favorite) {
        await deleteFavoriteProject(payload)
        projectDataLocal.value.is_favorite = false
      } else {
        await addFavoriteProject(payload)
        projectDataLocal.value.is_favorite = true
      }
    }

    watch(() => props.projectData, (newVal) => {
      projectDataLocal.value = JSON.parse(JSON.stringify(newVal))
    })

    return {
      statusColor,
      budgetBorderStyle,
      isFinancialDialogOpen,
      projectDataLocal,
      exportLoading,
      headerActions,
      detailFields,
      budgetFields,
      formatNumber,
      updateProject,
      toggleFavorite,
      icons: {
        mdiCheckboxBlankCircle,
        mdiHeartOutline,
        mdiHeart,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.min-height-32 {
  min-height: 32px !important;
}
.v-avatar-light-bg {
  background-color: rgba(var(--v-primary-base), 0.12) !important;
}
</style>